'use client'

import { createContext, PropsWithChildren, useContext, useState } from 'react'

import { useAuthContext } from '@/context/AuthContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import type { Room } from '@/services/cms'

type OwnerProfileContextType = {
  isPublicView: boolean
  setIsPublicView: (isPublicView: boolean) => void
  isOwner: boolean
  coOwnedRoomsPromise: Promise<Room[]>
  featuredRoomsPromise: Promise<Room[]>
}

export const OwnerProfileContext = createContext<OwnerProfileContextType | undefined>(undefined)

function normalizeUserId(value?: string) {
  return value?.trim().toLowerCase()
}

type OwnerProfileProviderProps = PropsWithChildren<{
  targetUserId?: string
  coOwnedRoomsPromise: Promise<Room[]>
  featuredRoomsPromise: Promise<Room[]>
}>

export function OwnerProfileProvider({
  targetUserId,
  coOwnedRoomsPromise,
  featuredRoomsPromise,
  children,
}: OwnerProfileProviderProps) {
  const [isPublicView, setIsPublicView] = useState(false)
  const { store } = useConfigsContext()
  const { isSignedIn } = useAuthContext()

  const currentUserId = store?.profile?.userId

  const isOwner = isSignedIn
    ? Boolean(normalizeUserId(currentUserId)) &&
      Boolean(normalizeUserId(targetUserId)) &&
      normalizeUserId(currentUserId) === normalizeUserId(targetUserId)
    : false

  return (
    <OwnerProfileContext.Provider
      value={{ isPublicView, setIsPublicView, isOwner, coOwnedRoomsPromise, featuredRoomsPromise }}
    >
      {children}
    </OwnerProfileContext.Provider>
  )
}

export function useOwnerProfileContext() {
  const context = useContext(OwnerProfileContext)
  if (!context) {
    throw new Error('useOwnerProfileContext must be used within OwnerProfileProvider')
  }
  return context
}
